/* Zeus add-ons (snippets/zeus-add-ons.liquid).
 *
 * Makes the ticked add-ons ride along with the main product when the customer
 * hits Add to cart, as separate line items.
 *
 * How: Dawn's product form builds its request with `new FormData(form)`, which
 * fires the form's `formdata` event first. We use that hook to rewrite the
 * payload from Shopify's single-item shape (id + quantity) to its multi-item
 * shape (items[n][id] + items[n][quantity]) - so Dawn's own submit, cart
 * drawer refresh and error handling all keep working untouched. No override of
 * Dawn's JavaScript.
 *
 * Also keeps the running "+$" total in the heading current, and lets an add-on
 * with several variants switch which variant gets added.
 */
(() => {
  const box = document.querySelector('[data-zeus-addons]');
  if (!box) return;

  // The buy form for this product. Dawn renders it in the same section; fall
  // back to the first add-to-cart form on the page.
  const section = box.closest('.shopify-section, section') || document;
  const form = section.querySelector('form[data-type="add-to-cart-form"]')
            || document.querySelector('form[data-type="add-to-cart-form"]');
  if (!form) return;

  const money = (cents) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

  const checks = () => [...box.querySelectorAll('[data-addon-check]')];
  const selected = () => checks()
    .filter((c) => c.checked && !c.disabled)
    .map((c) => {
      // Read the variant label off the select rather than caching it, so it is
      // right on first paint as well as after a change.
      const sel = c.closest('.zeus-addon')?.querySelector('[data-addon-variant]');
      const vt = sel ? sel.selectedOptions[0]?.textContent.trim().replace(/\s+—\s+sold out$/i, '') : '';
      const qtyEl = c.closest('.zeus-addon')?.querySelector('[data-addon-qty]');
      const n = parseInt(qtyEl?.value || '1', 10);
      const qty = Number.isFinite(n) && n > 0 ? Math.min(n, 99) : 1;
      return {
        id: c.dataset.variantId,
        price: Number(c.dataset.price),
        title: c.dataset.title,
        variantTitle: vt && vt !== 'Default Title' ? vt : '',
        qty,
      };
    });

  const total = box.querySelector('[data-addons-total]');
  const refresh = () => {
    const s = selected();
    const sum = s.reduce((a, b) => a + b.price * b.qty, 0);
    const units = s.reduce((a, b) => a + b.qty, 0);
    total.hidden = s.length === 0;
    total.textContent = s.length
      ? units + ' add-on' + (units > 1 ? 's' : '') + ' · +' + money(sum)
      : '';
  };

  // ---- running total -----------------------------------------------------
  const summary = document.querySelector('[data-zeus-summary]');
  const summaryLines = box.querySelector('[data-zeus-summary-lines]')
    || document.querySelector('[data-zeus-summary-lines]');
  const summaryTotal = document.querySelector('[data-zeus-summary-total]');
  let prices = null;
  try {
    prices = JSON.parse(document.querySelector('[data-zeus-prices]').textContent);
  } catch { /* summary stays hidden */ }

  const qtyValue = () => {
    const n = parseInt(document.querySelector('input[name="quantity"]')?.value || '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };
  const currentVariant = () => {
    const id = document.querySelector('.product-variant-id')?.value;
    return id && prices?.variants ? prices.variants[id] : null;
  };

  const renderSummary = () => {
    if (!summary || !summaryLines || !summaryTotal || !prices) return;
    const s = selected();
    const qty = qtyValue();

    // Only worth showing once the total differs from the price already on screen.
    if (!s.length && qty === 1) { summary.hidden = true; return; }
    summary.hidden = false;

    const v = currentVariant();
    const rows = [];
    let base = prices.title;
    if (v && v.t && v.t !== 'Default Title') base += ' — ' + v.t;
    if (qty > 1) base += ' × ' + qty;
    rows.push([base, (v ? v.p : 0) * qty]);
    for (const a of s)
      rows.push([
        a.title + (a.variantTitle ? ' — ' + a.variantTitle : '') + (a.qty > 1 ? ' × ' + a.qty : ''),
        a.price * a.qty,
      ]);

    summaryLines.textContent = '';
    for (const [label, cents] of rows) {
      const li = document.createElement('li');
      li.className = 'zeus-summary__line';
      const k = document.createElement('span');
      k.className = 'zeus-summary__label';
      k.textContent = label;
      const val = document.createElement('span');
      val.className = 'zeus-summary__value';
      val.textContent = money(cents);
      li.append(k, val);
      summaryLines.appendChild(li);
    }
    summaryTotal.textContent = money(rows.reduce((t, r) => t + r[1], 0));
  };

  // Delegated from document, not bound to the form or the field.
  //
  // Dawn's quantity input is associated by form="..." rather than being a DOM
  // descendant, so its events never bubble through the form element - and Dawn
  // replaces the quantity block wholesale when variant rules change, which would
  // drop a listener bound straight to the input. Delegation survives both.
  const watched = (t) =>
    t && (t.name === 'quantity' || (t.classList && t.classList.contains('product-variant-id')));
  document.addEventListener('change', (e) => {
    if (watched(e.target) || form.contains(e.target)) { syncScope(); renderSummary(); }
  });
  document.addEventListener('input', (e) => {
    if (watched(e.target)) renderSummary();
  });

  // ---- option scope ------------------------------------------------------
  // Some add-ons only fit some base options - the V2.0 ISQR fits the QRS rack
  // but not the XZS. A scoped-out tile is hidden AND unticked: leaving it
  // ticked-but-hidden would quietly add an incompatible part to the order.
  const tiles = () => [...box.querySelectorAll('.zeus-addon')];

  const inScope = (li) => {
    const raw = li.dataset.forOptions;
    if (!raw) return true;                     // no restriction recorded
    const v = currentVariant();
    if (!v || !Array.isArray(v.o)) return true; // cannot tell - show it
    const allowed = raw.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
    const chosen = v.o.map((x) => String(x).trim().toLowerCase());
    return allowed.some((a) => chosen.includes(a));
  };

  const syncScope = () => {
    let dropped = false;
    for (const li of tiles()) {
      const ok = inScope(li);
      li.hidden = !ok;
      const c = li.querySelector('[data-addon-check]');
      if (!ok && c && c.checked) { c.checked = false; dropped = true; }
    }
    // Nothing left to offer: hide the whole block rather than leave a heading.
    const anyVisible = tiles().some((li) => !li.hidden);
    box.hidden = !anyVisible;
    if (dropped) { refresh(); syncGallery(); }
  };

  // ---- gallery ----------------------------------------------------------
  // An add-on carrying data-media-id shows the product fitted with it. Driven
  // through Dawn's own media-gallery.setActiveMedia so the slider, thumbnails
  // and live region all stay in step - do not move the slides directly.
  const gallery = document.querySelector('media-gallery');

  // Media items are keyed "<section>-<mediaId>"; look the full key up rather
  // than reconstructing it, so this does not depend on the section id.
  const fullMediaId = (id) =>
    document.querySelector('.product__media-item[data-media-id$="-' + id + '"]')?.dataset.mediaId || null;

  const activeMediaId = () => {
    const el = document.querySelector('.product__media-item.is-active');
    const key = el?.dataset.mediaId;
    return key ? key.slice(key.lastIndexOf('-') + 1) : null;
  };

  let restoreTo = null;               // what was showing before we first switched
  let lastPicked = null;              // the add-on the customer ticked most recently

  const syncGallery = () => {
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    const withImage = checks().filter((c) => c.checked && !c.disabled && c.dataset.mediaId);
    // The one just ticked wins - not the last in DOM order. Ticking a second
    // add-on must show that second add-on, which list order alone gets wrong
    // whenever the newly ticked tile sits above one already ticked.
    const target =
      (lastPicked && lastPicked.checked && !lastPicked.disabled && lastPicked.dataset.mediaId
        ? lastPicked.dataset.mediaId
        : withImage.length ? withImage[withImage.length - 1].dataset.mediaId : null);

    if (target) {
      if (restoreTo === null) restoreTo = activeMediaId();
      const key = fullMediaId(target);
      if (key) gallery.setActiveMedia(key, true);
    } else if (restoreTo !== null) {
      const key = fullMediaId(restoreTo);
      if (key) gallery.setActiveMedia(key, true);
      restoreTo = null;
    }
  };

  // Stepper buttons. Clamped here rather than trusting the input: a customer can
  // type anything into a number field, and a blank or 0 would silently drop the
  // line from the cart while still showing as ticked.
  const clamp = (el) => {
    const n = parseInt(el.value, 10);
    el.value = String(Number.isFinite(n) ? Math.min(Math.max(n, 1), 99) : 1);
    const li = el.closest('.zeus-addon');
    const minus = li?.querySelector('[data-addon-qty-step="-1"]');
    if (minus) minus.disabled = Number(el.value) <= 1;
  };

  box.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-addon-qty-step]');
    if (!btn) return;
    e.preventDefault();
    const input = btn.closest('.zeus-addon')?.querySelector('[data-addon-qty]');
    if (!input) return;
    input.value = String((parseInt(input.value, 10) || 1) + Number(btn.dataset.addonQtyStep));
    clamp(input);
    refresh();
    renderSummary();
  });

  box.addEventListener('input', (e) => {
    if (!e.target.matches('[data-addon-qty]')) return;
    refresh();
    renderSummary();
  });
  box.addEventListener('blur', (e) => {
    if (!e.target.matches('[data-addon-qty]')) return;
    clamp(e.target);
    refresh();
    renderSummary();
  }, true);

  box.addEventListener('change', (e) => {
    if (e.target.matches('[data-addon-check]')) lastPicked = e.target.checked ? e.target : null;
    // A variant selector under a tile: point that tile's checkbox at the chosen
    // variant and show its price.
    if (e.target.matches('[data-addon-variant]')) {
      const opt = e.target.selectedOptions[0];
      const li = e.target.closest('.zeus-addon');
      const check = li.querySelector('[data-addon-check]');
      check.dataset.variantId = opt.value;
      check.dataset.price = opt.dataset.price;
      li.querySelector('[data-addon-price]').textContent = '+' + money(Number(opt.dataset.price));
      // Each variant is its own part, so the SKU moves with the selector.
      const skuEl = li.querySelector('[data-addon-sku]');
      if (skuEl) skuEl.textContent = opt.dataset.sku ? 'SKU: ' + opt.dataset.sku : '';
    }
    refresh();
    syncGallery();
    renderSummary();
  });

  // Add-ons are added in their OWN request, then the form is allowed through to
  // Dawn untouched.
  //
  // The previous approach rewrote Dawn's payload from Shopify's single-item
  // shape (id + quantity) to its multi-item shape (items[n][...]) in a formdata
  // hook. The items landed in the cart correctly - but the multi-item response
  // has no top-level `id`, and Dawn's cart notification looks up
  // `#cart-notification-product-${id}`. That returned null, threw inside
  // getSectionInnerHTML, and left the Add to cart button spinning forever with
  // no confirmation: a silent success that reads as a failure, and invites the
  // customer to click again and double-add.
  //
  // Doing our own request first and then letting Dawn make its ordinary
  // single-item add keeps its notification, its error handling and its button
  // state working. Do not reinstate the formdata rewrite.
  let passthrough = false;
  form.addEventListener(
    'submit',
    (e) => {
      if (passthrough) { passthrough = false; return; }
      const s = selected();
      if (!s.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      (async () => {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: s.map((a) => ({ id: a.id, quantity: a.qty })) }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.description || 'could not add the selected add-ons');
        }
        passthrough = true;
        form.requestSubmit(form.querySelector('[name="add"]'));
      })().catch((err) => {
        const box = form.querySelector('.product-form__error-message-wrapper');
        const msg = form.querySelector('.product-form__error-message');
        if (msg) msg.textContent = String(err.message);
        if (box) box.removeAttribute('hidden');
      });
    },
    true
  );

  syncScope();
  box.querySelectorAll('[data-addon-qty]').forEach(clamp);
  refresh();
  renderSummary();
})();
